import { parseToolbarQueryParam } from '../config/pacsConfig'
import { loadControllerSession } from '../services/controllerAuth'
import { CATALOG_DEFAULTS } from './catalogDefaults'
import {
  ENTITLEMENT_SOURCES,
  FEATURE_STATUS,
  UDV_CHROME_THEMES,
  UDV_MODES,
  UDV_PLANS,
} from './constants'
import { loadEntitlements } from './entitlements'
import {
  clampPlan,
  parsePlanFromSearchParams,
  parsePlanQueryParam,
  parsePortalFromSearchParams,
  parseShellFromSearchParams,
  parseTokenFromSearchParams,
} from './parsePlanQuery'
import {
  getCatalogOverrides,
  getPortalSim,
  getShowDraft,
  getSimulatedPlan,
  getSimulatedShell,
  getSimulatedTheme,
  getTokenOverrides,
} from './storage'
import { TOKENS_DEFAULTS } from './tokensDefaults'
import { parseThemeFromSearchParams } from './chromeTheme'

/**
 * @returns {'developer'|'production'}
 */
export function getUdvMode() {
  try {
    if (typeof window !== 'undefined' && window.location && window.location.hostname) {
      const host = String(window.location.hostname).toLowerCase()
      // Hosts desplegados: ignorar REACT_APP_UDV_MODE del build en dev-udv.
      if (host === 'udv.tanjousoft.com.ar') {
        return UDV_MODES.PRODUCTION
      }
      if (host.indexOf('dev-udv') >= 0) {
        return UDV_MODES.DEVELOPER
      }
    }
  } catch (e) {
    /* ignore */
  }

  try {
    if (typeof process !== 'undefined' && process.env && process.env.REACT_APP_UDV_MODE) {
      const m = String(process.env.REACT_APP_UDV_MODE).trim().toLowerCase()
      if (m === UDV_MODES.DEVELOPER || m === UDV_MODES.PRODUCTION) return m
    }
  } catch (e) {
    /* ignore */
  }
  try {
    if (typeof window !== 'undefined' && window.location && window.location.hostname) {
      const host = String(window.location.hostname).toLowerCase()
      if (
        host === 'localhost' ||
        host === '127.0.0.1'
      ) {
        return UDV_MODES.DEVELOPER
      }
    }
  } catch (e) {
    /* ignore */
  }
  return UDV_MODES.PRODUCTION
}

export function isDeveloperMode() {
  return getUdvMode() === UDV_MODES.DEVELOPER
}

/**
 * Privilegio staff Controller (`udv_lab_access` en /auth/me).
 * Eleva features a SUPREMO en prod sin activar modo developer del host.
 * @returns {boolean}
 */
export function hasUdvLabAccess() {
  try {
    const session = loadControllerSession()
    const user = session && session.user
    return !!(user && user.udv_lab_access === true)
  } catch (e) {
    return false
  }
}

/**
 * Plan root developer (dev-udv): por encima de premium; beta solo aquí.
 * @param {string|null|undefined} plan
 * @returns {boolean}
 */
export function isSupremoPlan(plan) {
  return parsePlanQueryParam(plan) === UDV_PLANS.SUPREMO
}

/**
 * SUPREMO con poder de lab: host developer o sesión staff Controlller.
 * @returns {boolean}
 */
function isLabSupremoSession() {
  return isDeveloperMode() || hasUdvLabAccess()
}

/**
 * Registry efectivo: production = solo defaults; developer = overrides ⊕ defaults.
 * @returns {import('./tokensDefaults').PlatformToken[]}
 */
export function getEffectiveTokenRegistry() {
  const defaults = TOKENS_DEFAULTS.slice()
  if (!isDeveloperMode()) return defaults
  const ov = getTokenOverrides()
  if (!ov || !ov.length) return defaults
  return ov
}

/**
 * @param {string|null|undefined} tokenStr
 * @returns {import('./tokensDefaults').PlatformToken|null}
 */
export function resolveToken(tokenStr) {
  if (!tokenStr) return null
  const list = getEffectiveTokenRegistry()
  const want = String(tokenStr).trim()
  for (let i = 0; i < list.length; i++) {
    if (list[i] && list[i].token === want) return list[i]
  }
  return null
}

/**
 * @returns {import('./catalogDefaults').PlanFeature[]}
 */
export function getEffectiveCatalog() {
  const base = CATALOG_DEFAULTS.map((f) => ({
    ...f,
    plans: (f.plans || []).slice(),
  }))
  if (!isDeveloperMode()) return base
  const ov = getCatalogOverrides()
  if (!ov || typeof ov !== 'object') return base
  return base.map((f) => {
    const patch = ov[f.id]
    if (!patch || typeof patch !== 'object') return f
    return {
      ...f,
      status: patch.status != null ? patch.status : f.status,
      plans: Array.isArray(patch.plans) ? patch.plans.slice() : f.plans,
      label: patch.label != null ? patch.label : f.label,
    }
  })
}

/**
 * Token efectivo desde URL (prioridad) o entitlements cacheados.
 * @param {{
 *   searchParams?: URLSearchParams|null,
 *   tokenMeta?: import('./tokensDefaults').PlatformToken|null,
 *   entitlements?: import('./entitlements').UdvEntitlements|null,
 * }} [opts]
 * @returns {import('./tokensDefaults').PlatformToken|null}
 */
export function resolveTokenMetaForAccess(opts = {}) {
  if (opts.tokenMeta !== undefined) return opts.tokenMeta || null
  const params =
    opts.searchParams ||
    (typeof window !== 'undefined'
      ? new URLSearchParams(window.location.search)
      : null)
  const fromUrl = parseTokenFromSearchParams(params)
  if (fromUrl) return resolveToken(fromUrl)
  const ent =
    opts.entitlements !== undefined ? opts.entitlements : loadEntitlements()
  if (ent && ent.udvToken) return resolveToken(ent.udvToken)
  return null
}

/**
 * Lab: perfil portal (patient) activo por ?portal=1/0 o switch en Settings.
 * Solo aplica en developer.
 * @param {{ searchParams?: URLSearchParams|null }} [opts]
 * @returns {boolean}
 */
export function isPortalProfileActive(opts = {}) {
  if (!isDeveloperMode()) return false
  const params =
    opts.searchParams ||
    (typeof window !== 'undefined'
      ? new URLSearchParams(window.location.search)
      : null)
  const fromUrl = parsePortalFromSearchParams(params)
  if (fromUrl === true) return true
  if (fromUrl === false) return false
  return getPortalSim()
}

/**
 * Prioridad: portal sim → lab_access SUPREMO → lab simulate/Supremo → entitlements → lite.
 * En developer, simulate (default Supremo) gana sobre entitlements del Edge/Gateway
 * para que el badge y Settings «Simular plan» coincidan (no quedar fijados en lite).
 * @param {{
 *   searchParams?: URLSearchParams|null,
 *   tokenMeta?: import('./tokensDefaults').PlatformToken|null,
 *   entitlements?: import('./entitlements').UdvEntitlements|null,
 * }} [opts]
 * @returns {string}
 */
export function getEffectivePlan(opts = {}) {
  const params =
    opts.searchParams ||
    (typeof window !== 'undefined'
      ? new URLSearchParams(window.location.search)
      : null)
  const ent =
    opts.entitlements !== undefined ? opts.entitlements : loadEntitlements()

  // 1) Lab: portal sim (URL o switch) → patient
  if (isPortalProfileActive({ searchParams: params })) {
    return UDV_PLANS.PATIENT
  }

  // 2) Staff Controller: plan efectivo UI SUPREMO (no muta entitlements del Edge).
  //    No elevar perfil portal / patient_share.
  if (hasUdvLabAccess()) {
    if (ent && ent.source !== ENTITLEMENT_SOURCES.URL) {
      const entPlan = parsePlanQueryParam(ent.plan)
      if (entPlan === UDV_PLANS.PATIENT) {
        return UDV_PLANS.PATIENT
      }
    }
    return UDV_PLANS.SUPREMO
  }

  // 3) Lab simulate (default Supremo). Antes que entitlements: si no, un Edge lite
  //    en Gateway deja el badge en LITE aunque Settings muestre «supremo (root)».
  if (isDeveloperMode()) {
    const fromUrl = parsePlanFromSearchParams(params)
    if (fromUrl === UDV_PLANS.SUPREMO) {
      return UDV_PLANS.SUPREMO
    }
    return getSimulatedPlan() || UDV_PLANS.SUPREMO
  }

  // 4) Entitlements (Controller / elevación / invite — nunca source:url legacy)
  if (ent && ent.plan) {
    if (ent.source === ENTITLEMENT_SOURCES.URL) {
      // ignore legacy URL cache
    } else {
      if (ent.udvToken) {
        const tMeta = resolveToken(ent.udvToken)
        if (tMeta && tMeta.enabled !== false) {
          return clampPlan(ent.plan, tMeta.plan || UDV_PLANS.LITE)
        }
      }
      return ent.plan
    }
  }

  return UDV_PLANS.LITE
}

/**
 * Hint de shell: URL → (lab simulate) → entitlements → token defaultShell.
 * @param {{
 *   searchParams?: URLSearchParams|null,
 *   tokenMeta?: import('./tokensDefaults').PlatformToken|null,
 *   entitlements?: import('./entitlements').UdvEntitlements|null,
 * }} [opts]
 * @returns {string|null}
 */
export function getEffectiveShellHint(opts = {}) {
  const params =
    opts.searchParams ||
    (typeof window !== 'undefined'
      ? new URLSearchParams(window.location.search)
      : null)
  const fromUrl = parseShellFromSearchParams(params)
  if (fromUrl) return fromUrl

  // Lab: Settings «Simular shell» gana sobre entitlements Edge (como plan).
  if (isDeveloperMode()) {
    const sim = getSimulatedShell()
    if (sim) return sim
  }

  const ent =
    opts.entitlements !== undefined ? opts.entitlements : loadEntitlements()
  if (ent && ent.shell) {
    const parsed = parseToolbarQueryParam(ent.shell)
    if (parsed) return parsed
  }

  const tokenMeta = resolveTokenMetaForAccess({
    searchParams: params,
    tokenMeta: opts.tokenMeta,
    entitlements: ent,
  })
  if (tokenMeta && tokenMeta.defaultShell) {
    return parseToolbarQueryParam(tokenMeta.defaultShell)
  }
  return null
}

/**
 * Theme chrome (OHIF/Oviyam): URL → (lab simulate) → entitlements → dark.
 * @param {{
 *   searchParams?: URLSearchParams|null,
 *   entitlements?: import('./entitlements').UdvEntitlements|null,
 * }} [opts]
 * @returns {'dark'|'brand'|'light'}
 */
export function getEffectiveTheme(opts = {}) {
  const params =
    opts.searchParams ||
    (typeof window !== 'undefined'
      ? new URLSearchParams(window.location.search)
      : null)
  const fromUrl = parseThemeFromSearchParams(params)
  if (fromUrl) return fromUrl

  if (isDeveloperMode()) {
    const sim = getSimulatedTheme()
    if (sim) return sim
  }

  const ent =
    opts.entitlements !== undefined ? opts.entitlements : loadEntitlements()
  if (ent && ent.theme) {
    return ent.theme
  }

  return UDV_CHROME_THEMES.DARK
}

/**
 * PACS efectivo: ?pacs= → entitlements.pacsId.
 * @param {{
 *   searchParams?: URLSearchParams|null,
 *   entitlements?: import('./entitlements').UdvEntitlements|null,
 * }} [opts]
 * @returns {string|null}
 */
export function getEffectivePacsId(opts = {}) {
  const params =
    opts.searchParams ||
    (typeof window !== 'undefined'
      ? new URLSearchParams(window.location.search)
      : null)
  if (params && typeof params.get === 'function') {
    const p = params.get('pacs')
    if (p != null && String(p).trim() !== '') {
      const v = String(p).trim()
      // Placeholders de plantilla lab
      if (v !== '{pacs}' && v.toLowerCase() !== 'pacs') return v
    }
  }
  const ent =
    opts.entitlements !== undefined ? opts.entitlements : loadEntitlements()
  if (ent && ent.pacsId) return ent.pacsId
  return null
}

/**
 * @param {string} featureId
 * @param {{
 *   plan?: string,
 *   searchParams?: URLSearchParams|null,
 *   showDraft?: boolean,
 * }} [opts]
 * @returns {boolean}
 */
export function canFeature(featureId, opts = {}) {
  const catalog = getEffectiveCatalog()
  const feature = catalog.find((f) => f.id === featureId)
  if (!feature) return false

  const plan = opts.plan || getEffectivePlan({ searchParams: opts.searchParams })
  const labAccess = hasUdvLabAccess()
  const showDraft =
    opts.showDraft != null
      ? opts.showDraft
      : labAccess
        ? true
        : isDeveloperMode()
          ? getShowDraft()
          : false

  if (feature.status === FEATURE_STATUS.DRAFT) {
    if (!isLabSupremoSession()) return false
    if (!isSupremoPlan(plan)) return false
    return showDraft
  }

  if (isSupremoPlan(plan) && isLabSupremoSession()) {
    return true
  }

  const plans = feature.plans || []
  return plans.indexOf(plan) >= 0
}

/**
 * Feature beta/draft: SUPREMO en developer o sesión staff (`udv_lab_access`).
 * @param {string} featureId
 * @param {{ plan?: string, searchParams?: URLSearchParams|null }} [opts]
 * @returns {boolean}
 */
export function canBetaFeature(featureId, opts = {}) {
  if (
    !isLabSupremoSession() ||
    !isSupremoPlan(opts.plan || getEffectivePlan({ searchParams: opts.searchParams }))
  ) {
    return false
  }
  const catalog = getEffectiveCatalog()
  const feature = catalog.find((f) => f.id === featureId)
  if (!feature) return false
  return feature.status === FEATURE_STATUS.DRAFT
}
